import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import type { StoredLevel } from '../../types/gameApi';
import { LevelCard } from './LevelCard';
import { getCompletedLevels } from '../../hooks/useGameEngine';
import { OrnamentDivider } from '../ui/OrnamentDivider';
import { LEVELS } from '../../data/levels';
import { useAuthStore } from '../../store/authStore';
import { isFreeAdmin } from '../../lib/freeAdmins';

interface LevelLobbyProps {
  levels: StoredLevel[];
  loading?: boolean;
  error?: boolean;
}

export function LevelLobby({ levels, loading, error }: LevelLobbyProps) {
  const { t } = useTranslation('game');
  const { user } = useAuthStore();
  const admin = isFreeAdmin(user?.email);
  const completed = getCompletedLevels();

  const unsolved = levels.filter((l) => !completed[l.levelId]);
  const solved   = levels.filter((l) => !!completed[l.levelId]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="flex flex-col gap-8"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl font-bold text-mafia-gold mb-2">
            {t('lobby.title')}
          </h1>
          <p className="text-mafia-paper-dim text-sm max-w-xl">
            {t('lobby.subtitle')}
          </p>
        </div>
        {admin && (
          <Link to="/admin/game" className="btn-gold text-xs whitespace-nowrap self-start mt-2">
            Управление уровнями
          </Link>
        )}
      </div>

      <OrnamentDivider />

      {loading && (
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-mafia-gold/40 border-t-mafia-gold rounded-full animate-spin" />
        </div>
      )}

      {error && (
        <p className="text-center text-mafia-muted text-sm py-8">
          Не удалось загрузить уровни. Попробуйте позже.
        </p>
      )}

      {!loading && !error && (
        <>
          {/* Unsolved levels */}
          {unsolved.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {unsolved.map((stored) => (
                <LevelCard key={stored.levelId} stored={stored} completed={false} />
              ))}
            </div>
          ) : levels.length > 0 ? (
            <p className="text-center text-mafia-gold/70 text-sm py-4 font-mono">
              Все уровни решены ✦
            </p>
          ) : null}

          {/* Fallback demo levels when nothing published */}
          {levels.length === 0 && (
            <>
              <p className="text-center text-mafia-muted text-xs font-mono uppercase tracking-widest -mb-4">
                Демо-уровни
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {LEVELS.map((level) => (
                  <Link
                    key={level.id}
                    to={`/game/${level.id}`}
                    className="card-vintage group block hover:border-mafia-gold/40 transition-colors p-5"
                  >
                    <div className="flex flex-col gap-2">
                      <span className="text-[9px] font-mono uppercase tracking-widest text-emerald-400 border border-emerald-700/40 bg-emerald-950/20 self-start px-1.5 py-0.5 rounded">
                        {level.difficulty}
                      </span>
                      <h3 className="font-display text-lg text-mafia-paper group-hover:text-mafia-gold transition-colors">
                        {level.title}
                      </h3>
                      <p className="text-xs text-mafia-muted line-clamp-2">{level.brief}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}

          {/* Solved levels */}
          {solved.length > 0 && (
            <>
              <OrnamentDivider variant="dim" />
              <div className="flex flex-col gap-3">
                <h2 className="font-mono text-xs uppercase tracking-widest text-mafia-muted">
                  Решённые ({solved.length})
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 opacity-70">
                  {solved.map((stored) => (
                    <LevelCard key={stored.levelId} stored={stored} completed={true} />
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </motion.div>
  );
}
